import { useCallback, useState } from "react";
import { ArrowUp } from "lucide-react";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";

interface TypeChatComposerProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  /** 键盘遮挡高度（px），由页面传入 useKeyboardBottomInset() */
  keyboardBottomInset?: number;
}

export function TypeChatComposer({
  onSend,
  disabled,
  keyboardBottomInset = 0,
}: TypeChatComposerProps) {
  const { theme, t } = useAppSettings();
  const isDark = theme === "dark";
  const [draft, setDraft] = useState("");

  const submit = useCallback(() => {
    const text = draft.trim();
    if (!text || disabled) return;
    onSend(text);
    setDraft("");
  }, [draft, disabled, onSend]);

  return (
    <div
      className={`pointer-events-auto fixed left-0 right-0 z-[1001] border-t px-4 pt-3 transition-[bottom] duration-200 ease-out ${
        isDark
          ? "border-white/10 bg-[#0c1220]/92 backdrop-blur-xl"
          : "border-gray-200/80 bg-white/92 backdrop-blur-xl"
      }`}
      style={{
        bottom: keyboardBottomInset,
        paddingBottom:
          keyboardBottomInset > 0
            ? "0.75rem"
            : "max(0.75rem, env(safe-area-inset-bottom, 0px))",
      }}
    >
      <div className="mx-auto flex max-w-xl items-end gap-2 pb-1">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          disabled={disabled}
          placeholder={t("whisper.typeChat.placeholder")}
          className={`min-h-[2.75rem] flex-1 resize-none rounded-2xl border px-4 py-3 text-sm leading-relaxed outline-none transition-[box-shadow] focus:ring-2 focus:ring-[#FFFBAA]/50 disabled:opacity-50 ${
            isDark
              ? "border-white/15 bg-white/10 text-white placeholder:text-white/40"
              : "border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400"
          }`}
          aria-label={t("whisper.typeChat.placeholder")}
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !draft.trim()}
          aria-label={t("whisper.typeChat.send")}
          className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FFFBAA] text-[#193182] shadow-md transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ArrowUp className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </button>
      </div>
    </div>
  );
}
